import { addDays, fromIso, toIso } from '@map/shared';
import { daysBetween } from './scoring.js';
/**
 * Strength of self: a count of days that goes up by one every day, by itself,
 * until it is reset.
 *
 * The day it began is day 1. A day it was reset is day 0, and the day after
 * is day 1 again. Nothing has to be logged for a day to count — holding is the
 * default, and only a reset is ever recorded. From day 40 a day is golden; the
 * count goes on for ever but the colour stops there.
 */
/** The run at which a day turns gold and the figure is wholly gold. */
export const STRENGTH_GOLD_DAYS = 40;
/** Whole days from `a` to `b`, both YYYY-MM-DD. */
function between(a, b) {
    return daysBetween(fromIso(a), fromIso(b));
}
/** Resets that count: on or after the day it began, once each, oldest first. */
export function strengthResets(state) {
    return [...new Set(state.resets)].filter((d) => d >= state.began).sort();
}
/** The count on `day`, or null before it began. */
export function strengthOn(state, day) {
    if (day < state.began)
        return null;
    let last = null;
    for (const r of strengthResets(state)) {
        if (r > day)
            break;
        last = r;
    }
    return last === null ? between(state.began, day) + 1 : between(last, day);
}
/** Every day from the beginning to `today`, oldest first. */
export function strengthSeries(state, today) {
    const resets = new Set(strengthResets(state));
    const out = [];
    let count = 0;
    for (let day = state.began; day <= today; day = toIso(addDays(fromIso(day), 1))) {
        const reset = resets.has(day);
        count = reset ? 0 : count + 1;
        out.push({ day, count, reset });
    }
    return out;
}
/** The longest run ever held, in days. */
export function strengthBest(state, today) {
    return strengthSeries(state, today).reduce((best, d) => Math.max(best, d.count), 0);
}
/** How golden the figure is, 0 (none) to 1 (wholly gold, from day 40). */
export function strengthGlow(count) {
    return Math.max(0, Math.min(1, count / STRENGTH_GOLD_DAYS));
}
/** A state read from a building's details, tolerating anything missing. */
export function strengthStateOf(details, fallbackBegan) {
    const d = (typeof details === 'object' && details !== null ? details : {});
    const iso = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
    return {
        began: iso(d.began) ? d.began : fallbackBegan,
        resets: Array.isArray(d.resets) ? d.resets.filter(iso) : [],
    };
}
//# sourceMappingURL=strength.js.map